import React, { useState } from 'react'
import { Pencil, Plus } from 'lucide-react'
import { ParentPicker, type ParentType } from '../../hierarchy/ParentPicker'
import { TaskService, type Task, type UpdateTaskRequest } from '../../../services/taskService'
import { CONTEXT_OPTIONS, DURATION_OPTIONS, ENERGY_OPTIONS, PRIORITY_OPTIONS, WHEN_OPTIONS, durationText, whenWords, fitWords, stakesWords } from '../../../utils/taskForm'
import { InlinePopover, ChipRow } from './InlinePopover'
import { WhyEditor, DoneMeansEditor, FirstStepEditor, StakesEditor, WhereEditor, RepeatsEditor } from './TaskWordEditors'

/**
 * Pass one of edit-in-place (owner 2026-09-05): the planning facts — when
 * (window or date, time, reminder lead), how long, energy and place, the
 * parent, priority — each edited where it is shown, and an "Add" row for
 * the ones not yet set. Every editor writes through the ordinary update
 * path and hands back the fresh task, so the score, summary and History
 * all move together.
 */
interface EditorProps {
  task: Task
  onChanged: (task: Task) => void
  /** What the page shows. Defaults to the current value's words. */
  trigger?: React.ReactNode
  variant?: 'dotted' | 'chip' | 'icon'
  className?: string
  /** When: open on "Pick a date" — a complete-by, not a window. */
  preferDate?: boolean
  openRequest?: number
}

async function write(task: Task, payload: UpdateTaskRequest, onChanged: (t: Task) => void) {
  const svc = new TaskService()
  await svc.updateTask(task.id, payload)
  onChanged(await svc.getTask(task.id))
}

const LEAD_OPTIONS = [
  { value: '0', label: 'At the time' },
  { value: '15', label: '15 min before' },
  { value: '30', label: '30 min before' },
  { value: '60', label: '1 hour before' },
  { value: '120', label: '2 hours before' },
  { value: '1440', label: 'A day before' },
]
const INTERVAL_OPTIONS = [
  { value: '15', label: 'every 15 min' },
  { value: '30', label: 'every 30 min' },
  { value: '60', label: 'hourly' },
]

const inputClass = 'min-h-9 rounded-md border border-soft bg-surface px-2 text-sm text-primary'

// ── When: window, or a date with time and reminder lead ────────────────────

export function WhenEditor({ task, onChanged, trigger, variant = 'dotted', className, preferDate = false, openRequest }: EditorProps) {
  const [when, setWhen] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('')
  const [lead, setLead] = useState('')
  const [interval, setInterval] = useState('30')
  const reset = () => {
    setWhen(task.due_date ? 'date' : preferDate ? 'date' : (task.target_window || ''))
    setDate(task.due_date ? task.due_date.slice(0, 10) : '')
    setTime(task.due_time || '')
    setLead(task.hold_lead_minutes != null ? String(task.hold_lead_minutes) : '')
    setInterval(task.hold_interval_minutes ? String(task.hold_interval_minutes) : '30')
  }
  const save = async () => {
    let payload: UpdateTaskRequest
    if (when === 'date') {
      if (!date) throw new Error('Pick the date.')
      payload = {
        due_date: date, target_window: null,
        due_time: time || null,
        hold_lead_minutes: time && lead !== '' ? Number(lead) : null,
        hold_interval_minutes: time && lead !== '' ? Number(interval) : null,
      }
    } else if (when === 'today') payload = { target_window: 'today', user_tier: 'committed', due_date: null, due_time: null, hold_lead_minutes: null }
    else if (when === 'someday') payload = { target_window: 'someday', user_tier: 'backlog', due_date: null, due_time: null, hold_lead_minutes: null }
    else if (when === 'this_week' || when === 'this_month') payload = { target_window: when, user_tier: null, due_date: null, due_time: null, hold_lead_minutes: null }
    else payload = { target_window: null, due_date: null, due_time: null, hold_lead_minutes: null }
    await write(task, payload, onChanged)
  }
  return (
    <InlinePopover label={preferDate ? 'Complete by' : 'When'} variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-when" openRequest={openRequest}
                   trigger={trigger ?? (whenWords(task) || <><Plus className="h-3 w-3" /> when</>)}>
      <ChipRow label="When" options={WHEN_OPTIONS} value={when} onChange={setWhen} />
      {when === 'date' && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Which date" className={inputClass} />
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} aria-label="Time (optional)" className={inputClass} />
          </div>
          {time && (
            <div className="flex flex-wrap items-center gap-2 text-app-micro text-muted">
              <span>Remind me</span>
              <select value={lead} onChange={(e) => setLead(e.target.value)} aria-label="Start reminding" className={inputClass}>
                <option value="">not at all</option>
                {LEAD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {lead !== '' && (
                <select value={interval} onChange={(e) => setInterval(e.target.value)} aria-label="Remind every" className={inputClass}>
                  {INTERVAL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
      )}
    </InlinePopover>
  )
}

// ── How long ───────────────────────────────────────────────────────────────

export function DurationEditor({ task, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const [custom, setCustom] = useState('')
  const reset = () => {
    const m = task.estimated_duration || 0
    const known = DURATION_OPTIONS.some((o) => Number(o.value) === m)
    setValue(known ? String(m) : (m ? 'custom' : ''))
    setCustom(known || !m ? '' : String(m))
  }
  const save = async () => {
    const minutes = value === 'custom' ? Number(custom) : Number(value)
    if (!minutes || minutes <= 0) throw new Error('Pick or type a duration.')
    await write(task, { estimated_duration: Math.round(minutes) }, onChanged)
  }
  const est = durationText(task.estimated_duration)
  return (
    <InlinePopover label="How long" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-duration"
                   trigger={trigger ?? (est ? `Takes ${est}` : <><Plus className="h-3 w-3" /> how long</>)}>
      <ChipRow label="How long" options={[...DURATION_OPTIONS, { value: 'custom', label: 'More…' }]} value={value} onChange={setValue} />
      {value === 'custom' && (
        <input type="number" min={5} step={5} value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="minutes" aria-label="Minutes" className={`${inputClass} w-28`} />
      )}
    </InlinePopover>
  )
}

// ── Fits: energy and place ─────────────────────────────────────────────────

export function FitEditor({ task, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [energy, setEnergy] = useState('')
  const [context, setContext] = useState('')
  const reset = () => { setEnergy(task.energy_level_required || ''); setContext(task.context || '') }
  const save = async () => {
    if (!energy && !context) throw new Error('Pick the energy it takes, or where it happens.')
    await write(task, { energy_level_required: (energy || null) as UpdateTaskRequest['energy_level_required'], context: context || null }, onChanged)
  }
  return (
    <InlinePopover label="Fits" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-fit"
                   trigger={trigger ?? (fitWords(task) || <><Plus className="h-3 w-3" /> energy · place</>)}>
      <div>
        <div className="mb-1 text-app-micro text-muted">Energy it takes</div>
        <ChipRow label="Energy" options={ENERGY_OPTIONS} value={energy} onChange={setEnergy} />
      </div>
      <div>
        <div className="mb-1 text-app-micro text-muted">Where</div>
        <ChipRow label="Where" options={CONTEXT_OPTIONS} value={context} onChange={setContext} />
      </div>
    </InlinePopover>
  )
}

// ── Parent: goal or skill ──────────────────────────────────────────────────

export function ParentEditor({ task, onChanged, trigger, variant = 'icon', className }: EditorProps) {
  const [parentType, setParentType] = useState<ParentType | ''>('')
  const [parentId, setParentId] = useState<number | null>(null)
  const reset = () => {
    setParentType(task.parent_goal_id ? 'goal' : task.parent_skill_id ? 'skill' : '')
    setParentId(task.parent_goal_id ?? task.parent_skill_id ?? null)
  }
  const save = async () => {
    const payload: UpdateTaskRequest = parentType === 'goal' && parentId
      ? { parent_goal_id: parentId, parent_skill_id: null }
      : parentType === 'skill' && parentId
        ? { parent_skill_id: parentId, parent_goal_id: null }
        : { parent_goal_id: null, parent_skill_id: null }
    await write(task, payload, onChanged)
  }
  return (
    <InlinePopover label="Belongs to" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-parent" width="w-96"
                   trigger={trigger ?? (variant === 'icon' ? <Pencil className="h-3 w-3" /> : <><Plus className="h-3 w-3" /> belongs to</>)}>
      <ParentPicker allowedTypes={['goal', 'skill']} parentType={parentType} parentId={parentId}
                    onChange={(f) => { setParentType((f.parent_type as ParentType) || ''); setParentId(f.parent_id ? Number(f.parent_id) : null) }} />
      {(task.parent_goal_id || task.parent_skill_id) && (
        <button type="button" onClick={() => { setParentType(''); setParentId(null) }} className="text-app-micro text-muted underline decoration-dotted hover:text-secondary">
          Stand on its own (no parent)
        </button>
      )}
    </InlinePopover>
  )
}

// ── Priority ───────────────────────────────────────────────────────────────

export function PriorityEditor({ task, onChanged, trigger, variant = 'dotted', className }: EditorProps) {
  const [value, setValue] = useState('')
  const reset = () => setValue(task.priority || 'medium')
  const save = async () => {
    if (!value) throw new Error('Pick one.')
    await write(task, { priority: value as UpdateTaskRequest['priority'] }, onChanged)
  }
  const label = PRIORITY_OPTIONS.find((o) => o.value === task.priority)?.label || 'Normal'
  return (
    <InlinePopover label="Priority" variant={variant} className={className} onOpen={reset} onSave={save} testId="task-edit-priority" width="w-72"
                   trigger={trigger ?? label}>
      <ChipRow label="Priority" options={PRIORITY_OPTIONS} value={value} onChange={setValue} />
    </InlinePopover>
  )
}

// ── "Add" row: the planning facts not yet set ──────────────────────────────

export function AddDetailsRow({ task, onChanged }: { task: Task; onChanged: (task: Task) => void }) {
  // Owner 2026-09-05: related things sit together. Time and place first
  // (when, what slips, how long, where, energy · place, repeats), then
  // weight and belonging (priority, belongs to, why), then the doing
  // (first step, done means). Groups are set apart by a wider gap.
  const open = task.status !== 'in_progress'
  const timeAndPlace: React.ReactNode[] = []
  const needsCompleteBy = !task.due_date && (task.priority === 'urgent' || task.priority === 'high')
  if (needsCompleteBy) timeAndPlace.push(<WhenEditor key="complete-by" task={task} onChanged={onChanged} variant="chip" preferDate trigger={<><Plus className="h-3 w-3" /> complete by</>} />)
  else if (!task.due_date && !task.target_window && open) timeAndPlace.push(<WhenEditor key="when" task={task} onChanged={onChanged} variant="chip" />)
  if (task.due_date && !stakesWords(task)) timeAndPlace.push(<StakesEditor key="stakes" task={task} onChanged={onChanged} variant="chip" />)
  if (!task.estimated_duration) timeAndPlace.push(<DurationEditor key="duration" task={task} onChanged={onChanged} variant="chip" />)
  if (!task.location && !task.place_id) timeAndPlace.push(<WhereEditor key="where" task={task} onChanged={onChanged} variant="chip" />)
  if (!task.energy_level_required && !task.context) timeAndPlace.push(<FitEditor key="fit" task={task} onChanged={onChanged} variant="chip" />)
  if (!task.is_recurring) timeAndPlace.push(<RepeatsEditor key="repeats" task={task} onChanged={onChanged} variant="chip" />)

  const weight: React.ReactNode[] = []
  if (task.priority !== 'high' && task.priority !== 'urgent') {
    const current = PRIORITY_OPTIONS.find((o) => o.value === task.priority)?.label || 'Normal'
    weight.push(<PriorityEditor key="priority" task={task} onChanged={onChanged} variant="chip" trigger={<>priority · {current}</>} />)
  }
  if (!task.parent_goal_id && !task.parent_skill_id) weight.push(<ParentEditor key="parent" task={task} onChanged={onChanged} variant="chip" />)
  if (!task.motivation) weight.push(<WhyEditor key="why" task={task} onChanged={onChanged} variant="chip" />)

  const doing: React.ReactNode[] = []
  if (!task.minimum_starter_step) doing.push(<FirstStepEditor key="first" task={task} onChanged={onChanged} variant="chip" />)
  if (!task.acceptance_criteria) doing.push(<DoneMeansEditor key="done" task={task} onChanged={onChanged} variant="chip" />)

  const groups = [timeAndPlace, weight, doing].filter((g) => g.length > 0)
  if (groups.length === 0) return null
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="task-add-details">
      <span className="text-app-micro text-muted">Add</span>
      {groups.map((g, i) => (
        <span key={i} className="flex flex-wrap items-center gap-1.5" data-testid="task-add-group">{g}</span>
      ))}
    </div>
  )
}
